import { useMutation } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputText from "@repo/components/src/forms/text";
import {
  Button,
  ButtonSpinner,
  ButtonText,
} from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { ChangeSignUpEmailDocument } from "@repo/graphql/documents/user/mutation.generated";
import { Link } from "expo-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { applyFieldErrors, toFieldErrors } from "../userErrors";

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Revisa tu correo: no parece válido.")
    .required("Ingresa tu correo."),
});

type Values = yup.InferType<typeof schema>;

type Props = {
  onChanged: (email: string) => void;
  onCancel: () => void;
};

const CHANGE_FAILED = "No pudimos cambiar tu correo. Inténtalo de nuevo.";

/**
 * «¿No es tu correo?»: moves the account this browser just signed up to another
 * address and emails the login link there (`changeSignUpEmail`). The backend refuses
 * once the link was opened or the change window passed, or for an address that
 * already has an account; any refusal offers to log in instead.
 */
const ChangeEmailForm = ({ onChanged, onCancel }: Props) => {
  const [formError, setFormError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const { control, handleSubmit, setError } = useForm<Values>({
    mode: "onTouched",
    resolver: yupResolver(schema),
    defaultValues: { email: "" },
  });
  const [changeSignUpEmail, { loading }] = useMutation(
    ChangeSignUpEmailDocument,
  );

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    setRefused(false);
    try {
      const { data } = await changeSignUpEmail({ variables: { email } });
      const payload = data?.user.changeSignUpEmail;
      const errors = toFieldErrors(payload?.userErrors ?? []);
      if (errors.length > 0) {
        setRefused(true);
        setFormError(applyFieldErrors(setError, errors, { email: "email" }));
        return;
      }
      onChanged(payload?.email ?? email);
    } catch {
      setFormError(CHANGE_FAILED);
    }
  });

  return (
    <VStack space="sm" className="w-full">
      <FormInputText
        control={control}
        name="email"
        label="Tu correo"
        required
        placeholder="tu@correo.com"
        keyboardType="email-address"
        textContentType="emailAddress"
        autoCapitalize="none"
        disabled={loading}
      />
      {formError ? (
        <Text role="alert" size="sm" className="text-error-600">
          {formError}
        </Text>
      ) : null}
      {refused ? (
        <Link href={{ pathname: "/" }} asChild>
          <Button
            action="brand"
            variant="link"
            size="sm"
            className="self-start"
          >
            <ButtonText>Iniciar sesión</ButtonText>
          </Button>
        </Link>
      ) : null}
      <HStack space="sm">
        <Button
          action="muted"
          variant="outline"
          rounded="lg"
          className="flex-1"
          isDisabled={loading}
          onPress={onCancel}
        >
          <ButtonText>Cancelar</ButtonText>
        </Button>
        <Button
          action="brand"
          variant="solid"
          rounded="lg"
          className="flex-1"
          isDisabled={loading}
          onPress={() => void onSubmit()}
        >
          {loading ? <ButtonSpinner /> : null}
          <ButtonText>Enviar enlace</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
};

export default ChangeEmailForm;
